import { Component, Optional, OnInit, ChangeDetectorRef } from '@angular/core';
import { MdDialog, MdDialogRef, MdSnackBar } from '@angular/material';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Observable } from 'rxjs/Observable';

import { ApplicationValue } from 'app/services/warmup/application-type.model';
import { WarmUpService, ApplicationTypeConstants } from 'app/services/warmup/warmup.service';
import { isValidMailFormat } from 'app/shared/validators/generic-validators';
import { ConstantsService } from 'app/services/constants-service';
import { CanComponentDeactivate } from 'app/services/can-deactivate.service';
import { CookieService } from 'app/services/cookie.service';
import { SnackBarService } from 'app/services/snackbar.service';
import { MessageService } from 'app/services/messages.service';
import { TrackingConstants } from 'app/shared/services/tracking/tracking-constants.service';
import { TrackingService } from 'app/shared/services/tracking/google-analytics-events.service';

import { UserPreferencesModel } from './user-preferences.model';

import { UserPreferencesService } from './user-preferences.service';

@Component({
  selector: 'app-user-preference',
  providers: [UserPreferencesService],
  templateUrl: './user-preferences.component.html',
  styleUrls: ['./user-preferences.component.css']
})

/**
 * Component for displaying and saving user preferences.
 */
export class UserPreferencesComponent implements OnInit, CanComponentDeactivate {
  userPrefForm: FormGroup;
  languages: ApplicationValue[];
  connectionModes: ApplicationValue[];
  userPreferences: UserPreferencesModel;
  savingUserPrefs = false;

  MS_EXCHANGE = 'Microsoft Exchange EWS';
  IMAP = 'IMAP';

  formErrors = {
    'displayname': '',
    'language': '',
    'msExchange.msexchangeemail': '',
    'msExchange.msexchangeewsurl': '',
    'imap.imapemail': '',
    'imap.imaphost': '',
    'imap.imapport': '',
  };

  validationMessages = {
    'displayname': {
      'required': 'Required field value.',
    },
    'language': {
      'required': 'Required field value.',
    },
    'msExchange.msexchangeemail': {
      'required': 'Required field value.',
      'invalidEmail': 'Invalid email format.',
    },
    'msExchange.msexchangeewsurl': {
      'required': 'Required field value.',
    },
    'imap.imapemail': {
      'required': 'Required field value.',
      'invalidEmail': 'Invalid email format.',
    },
    'imap.imaphost': {
      'required': 'Required field value.',
    },
    'imap.imapport': {
      'required': 'Required field value.',
    }
  };

  constructor(private fb: FormBuilder,
    private warmUpService: WarmUpService,
    private userPrefService: UserPreferencesService,
    private snackBarService: SnackBarService,
    private cdr: ChangeDetectorRef,
    private cookieService: CookieService,
    private trackingService: TrackingService) { }

  ngOnInit(): void {
    this.buildForm();
    this.setUpLanguages(this.warmUpService.getApplicationValue(ApplicationTypeConstants.LANGUAGE));
    this.setUpConnectionModes(this.warmUpService.getApplicationValue(ApplicationTypeConstants.CONNECTION_MODE));
    this.userPrefService
      .getUserPreferences(this.cookieService.get(ConstantsService.COOKIE_LOGIN_USER_NAME))
      .subscribe(response => {
        // @todo: e-mail validators should only be mandatory, if an e-mail preference is enabled!
        this.userPrefForm.patchValue({
          displayname: response.displayName,
          language: response.language.id,
          connectionmode: response.connectionMode.name,
          msExchange: {
            msexchangeemail: response.email,
            msexchangeewsurl: response.exchangeEwsUrl
          },
          imap: {
            imapemail: response.imapEmailId,
            imaphost: response.imapHost,
            imapport: response.imapPort
          }
        });
        this.cdr.detectChanges();
        // ensures that validators are triggerd.
        for (let inner in this.userPrefForm.controls) {
          this.userPrefForm.get(inner).markAsTouched();
          this.userPrefForm.get(inner).updateValueAndValidity();
        }
      });
  }

  setUpConnectionModes(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.connectionModes = this.removePopFormConnectionModeList(response.values);
          this.cdr.detectChanges();
        }
      );
    } else {
      this.connectionModes = this.removePopFormConnectionModeList(dropDownValues);
    }
  }

  removePopFormConnectionModeList(values) {
    return values.filter(item => item.name !== 'POP');
  }

  setUpLanguages(dropDownValues: any) {
    if (dropDownValues instanceof Observable) {
      dropDownValues.subscribe(
        response => {
          this.warmUpService.setApplicationTypeInLS(response);
          this.languages = response.values;
          this.cdr.detectChanges();
        }
      );
    } else {
      this.languages = dropDownValues;
    }
  }

  buildForm() {
    this.userPrefForm = this.fb.group({
      displayname: ['', Validators.required],
      language: ['', Validators.required],
      connectionmode: '',
      msExchange: this.fb.group({
        msexchangeemail: ['',
          Validators.compose([
            Validators.required, isValidMailFormat
          ])
        ],
        msExchangePwd: '',
        msexchangeewsurl: ['', Validators.required]
      }),
      imap: this.fb.group({
        imapemail: ['',
          Validators.compose([
            Validators.required, isValidMailFormat
          ])
        ],
        imaphost: ['', Validators.required],
        imapport: ['', Validators.required]
      })
    });

    this.userPrefForm.valueChanges.subscribe(data => this.onValueChanged(data));
    this.onValueChanged(); // (re)set validation messages now
  }

  onValueChanged(data?: any) {
    if (!this.userPrefForm) {
      return;
    }
    const form = this.userPrefForm;

    for (const field in this.formErrors) {
      // clear previous error message (if any)
      this.formErrors[field] = '';
      const control = form.get(field);
      if (control && control.touched && !control.valid) {
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + ' ';
        }
      }
    }
  }

  onButtonClick(buttonValue) {
    // TODO(pankaj.khurana): Need to handle below code in a better way.
    const confirmTabChange = confirm(MessageService.MAILBOX_TAB_CHANGE_ALERT);
    if (confirmTabChange) {
      if (buttonValue === this.MS_EXCHANGE) {
        this.userPrefForm.get('imap').markAsPristine;
        this.resetImapTab();
      } else if (buttonValue === this.IMAP) {
        this.userPrefForm.get('msExchange').markAsPristine;
        this.resetMsExchangeTab();
      }
    } else {
      return false;
    }
  }

  onSubmit({ value, valid }) {
    this.savingUserPrefs = true;
    const userPreferencesModel = new UserPreferencesModel();
    userPreferencesModel.id = 0;
    userPreferencesModel.displayName = value.displayname;
    userPreferencesModel.language = this.getLanguageAV(value.language);
    userPreferencesModel.connectionMode =
      this.getConnectionModeAV(value.connectionmode);
    // TODO(pankaj.khurana): Need to handle the below code in a better way.
    if (value.connectionmode === this.MS_EXCHANGE) {
      userPreferencesModel.email = value.msExchange.msexchangeemail;
      userPreferencesModel.exchangeEwsUrl = value.msExchange.msexchangeewsurl;
      this.removeImapFields(userPreferencesModel);
    } else if (value.connectionmode === this.IMAP) {
      userPreferencesModel.imapHost = value.imap.imaphost;
      userPreferencesModel.imapEmailId = value.imap.imapemail;
      userPreferencesModel.imapPort = value.imap.imapport;
      this.removeMsExchangeFields(userPreferencesModel);
    }
    this.userPrefService.saveUserPreferences(userPreferencesModel)
      .subscribe(
      success => {
        this.savingUserPrefs = false;
        this.callSnackBarService(success,
          MessageService.SAVE_PREFERENCES_SUCCESS,
          MessageService.SAVE_PREFERENCES_FAILURE);
        this.userPrefForm.markAsPristine();
        this.trackingService
          .emitEvent(TrackingConstants.USER_PREFERENCES, TrackingConstants.SAVE_USER_PREFERENCES, TrackingConstants.SUCCESS, 1);
      },
      err => {
        this.savingUserPrefs = false;
        this.callSnackBarService(err,
          MessageService.SAVE_PREFERENCES_SUCCESS,
          MessageService.SAVE_PREFERENCES_FAILURE);
        this.trackingService
          .emitEvent(TrackingConstants.USER_PREFERENCES, TrackingConstants.SAVE_USER_PREFERENCES, TrackingConstants.FAILURE, 0);
      }
      );
  }

  callSnackBarService(response: any, successMsg: string, failureMsg: string) {
    this.snackBarService.showStatus(response, successMsg, failureMsg);
  }

  private resetImapTab() {
    this.userPrefForm.get('imap.imapemail').reset();
    this.userPrefForm.get('imap.imaphost').reset();
    this.userPrefForm.get('imap.imapport').reset();
  }

  private resetMsExchangeTab() {
    this.userPrefForm.get('msExchange.msexchangeemail').reset();
    this.userPrefForm.get('msExchange.msexchangeewsurl').reset();
    this.userPrefForm.get('msExchange.msExchangePwd').reset();
  }

  private getConnectionModeAV(connectionModeName: string): ApplicationValue {
    const connectionModeAV = new ApplicationValue();
    this.connectionModes.forEach((connectionMode, index) => {
      if (connectionMode.name === connectionModeName) {
        connectionModeAV.id = connectionMode.id;
        connectionModeAV.name = connectionMode.name;
      }
    });
    return connectionModeAV;
  }

  private getLanguageAV(languageId: number): ApplicationValue {
    const languageAV = new ApplicationValue();
    this.languages.forEach((language, index) => {
      if (language.id === languageId) {
        languageAV.id = language.id;
        languageAV.name = language.name;
      }
    });
    return languageAV;
  }

  private removeImapFields(userPreferencesModel): UserPreferencesModel {
    userPreferencesModel.imaphost = undefined;
    userPreferencesModel.imapemail = undefined;
    userPreferencesModel.imapport = undefined;
    return userPreferencesModel;
  }

  private removeMsExchangeFields(userPreferencesModel): UserPreferencesModel {
    userPreferencesModel.msexchangeemail = undefined;
    userPreferencesModel.msexchangeewsurl = undefined;
    return userPreferencesModel;
  }

  getUserPreferencesValidity(): boolean {
    return this.userPrefForm.get('displayname').valid
      && this.userPrefForm.get('language').valid
      && (this.userPrefForm.get('msExchange').valid
        || this.userPrefForm.get('imap').valid);
  }

  onReset() {
    if (confirm(MessageService.RESET_USER_PREFERENCES_ALERT)) {
      this.userPrefForm.reset();
      this.trackingService
        .emitEvent(TrackingConstants.USER_PREFERENCES, TrackingConstants.SAVE_USER_PREFERENCES, TrackingConstants.SUCCESS, 1);
    }
  }

  canDeactivate(): Observable<boolean> | boolean {
    if (this.userPrefForm.dirty) {
      return confirm(MessageService.UNSAVED_FORM_ALERT);
    }
    return true;
  }

  autoDetectMSUrl() {
    this.userPrefService.getMSExchangeUrl(
      this.userPrefForm.get('msExchange.msexchangeemail').value,
      this.userPrefForm.get('msExchange.msExchangePwd').value)
      .subscribe(response => {
        this.userPrefForm.get('msExchange.msexchangeewsurl').setValue(response.arrResult);
        this.callSnackBarService(response,
          MessageService.AUTO_DETECT_EWS_URL_SUCCESS,
          MessageService.AUTO_DETECT_EWS_URL_FAILURE);

      },
      err => {
        this.callSnackBarService(err,
          MessageService.AUTO_DETECT_EWS_URL_SUCCESS,
          MessageService.AUTO_DETECT_EWS_URL_FAILURE);
      });
  }
}



// WEBPACK FOOTER //
// ./src/app/header/usersettings/userpreferences/user-preferences.component.ts